'use client';

import React, { useState } from 'react';
import { ShieldCheck, RotateCcw, User, BookOpen, Building2, Hash, GraduationCap, BadgeCheck, Printer } from 'lucide-react';

type Diploma = {
  id: string;
  nome: string;
  cpf: string;
  cpfFormatado: string;
  numeroDiploma: string;
  curso: string;
  habilitacao: string;
  instituicao: string;
  siglaInstituicao: string;
  municipioInstituicao: string;
  dataColacao: string;
  dataRegistro: string;
  numeroRegistro: string;
  situacao: 'válido';
  modalidade: string;
  cargaHoraria: string;
  anoIngresso: string;
};

type Props = {
  diploma: Diploma;
  onNovaConsulta: () => void;
};

const FIELD_GROUPS = [
  {
    groupId: 'group-formando',
    title: 'Dados do Formando',
    icon: User,
    fields: [
      { id: 'f-nome', label: 'Nome Completo', key: 'nome' as keyof Diploma, mono: false },
      { id: 'f-cpf', label: 'CPF', key: 'cpfFormatado' as keyof Diploma, mono: true },
    ],
  },
  {
    groupId: 'group-curso',
    title: 'Dados do Curso',
    icon: BookOpen,
    fields: [
      { id: 'f-curso', label: 'Curso', key: 'curso' as keyof Diploma, mono: false },
      { id: 'f-hab', label: 'Habilitação', key: 'habilitacao' as keyof Diploma, mono: false },
      { id: 'f-mod', label: 'Modalidade', key: 'modalidade' as keyof Diploma, mono: false },
      { id: 'f-ch', label: 'Carga Horária', key: 'cargaHoraria' as keyof Diploma, mono: false },
      { id: 'f-ingresso', label: 'Ano de Ingresso', key: 'anoIngresso' as keyof Diploma, mono: false },
    ],
  },
  {
    groupId: 'group-inst',
    title: 'Instituição de Ensino',
    icon: Building2,
    fields: [
      { id: 'f-inst', label: 'Instituição', key: 'instituicao' as keyof Diploma, mono: false },
      { id: 'f-sigla', label: 'Sigla', key: 'siglaInstituicao' as keyof Diploma, mono: true },
      { id: 'f-mun', label: 'Município', key: 'municipioInstituicao' as keyof Diploma, mono: false },
    ],
  },
  {
    groupId: 'group-registro',
    title: 'Registro do Diploma',
    icon: Hash,
    fields: [
      { id: 'f-ndip', label: 'Número do Diploma', key: 'numeroDiploma' as keyof Diploma, mono: true },
      { id: 'f-nreg', label: 'Número de Registro', key: 'numeroRegistro' as keyof Diploma, mono: true },
      { id: 'f-col', label: 'Data de Colação de Grau', key: 'dataColacao' as keyof Diploma, mono: false },
      { id: 'f-reg', label: 'Data de Registro', key: 'dataRegistro' as keyof Diploma, mono: false },
    ],
  },
];

export default function DiplomaResultCard({ diploma, onNovaConsulta }: Props) {
  const [diplomaModalOpen, setDiplomaModalOpen] = useState(false);

  return (
    <div
      className="bg-card border-2 rounded-xl shadow-lg overflow-hidden"
      style={{ borderColor: '#E8002D' }}
      role="region"
      aria-label="Resultado da consulta de diploma"
    >
      {/* Valid badge header — light green background */}
      <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #4ADE80 0%, #22C55E 100%)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 bg-white rounded-full flex items-center justify-center seal-glow"
            aria-hidden="true"
          >
            <ShieldCheck size={22} style={{ color: '#22C55E' }} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif', color: '#FACC15' }}>
              Status do Diploma
            </p>
            <p className="text-lg font-extrabold leading-tight flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif', color: '#FACC15' }}>
              <BadgeCheck size={18} aria-hidden="true" />
              DIPLOMA VÁLIDO
            </p>
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end">
          <span className="text-xs font-medium" style={{ fontFamily: 'Open Sans, sans-serif', color: '#FACC15' }}>
            Registro verificado em
          </span>
          <span className="text-sm font-bold font-mono" style={{ color: '#FACC15' }}>
            17/08/2026
          </span>
        </div>
      </div>

      {/* Accent bar */}
      <div className="accent-bar" aria-hidden="true" />

      {/* Content */}
      <div className="p-6 sm:p-8 space-y-8">
        {FIELD_GROUPS.map((group) => (
          <div key={group.groupId}>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
              <group.icon size={15} style={{ color: '#06B6D4' }} aria-hidden="true" />
              <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#06B6D4', fontFamily: 'Montserrat, sans-serif' }}>
                {group.title}
              </h3>
            </div>
            <div className="diploma-field-row">
              {group.fields.map((field) => (
                <div key={field.id} className="flex flex-col">
                  <span className="field-label">{field.label}</span>
                  <span className={field.mono ? 'field-value-mono' : 'field-value'}>
                    {String(diploma[field.key])}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Authenticity notice */}
        <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#FFF0F3', border: '1px solid #FECDD3' }}>
          <ShieldCheck size={16} style={{ color: '#E8002D' }} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs leading-relaxed" style={{ color: '#7F1D1D', fontFamily: 'Open Sans, sans-serif' }}>
            Este registro foi verificado contra a base de dados do sistema.
            A autenticidade deste diploma é confirmada pelo número de registro{' '}
            <span className="font-mono font-bold">{diploma.numeroRegistro}</span>.
            Em caso de dúvidas, entre em contato com a instituição emissora.
          </p>
        </div>

        {/* Diploma stamp */}
        <button
          onClick={() => setDiplomaModalOpen(true)}
          className="w-full flex items-center justify-center py-4 border-2 border-dashed rounded-xl cursor-pointer hover:bg-cyan-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          style={{ borderColor: '#06B6D4' }}
          aria-label="Ver diploma autenticado"
        >
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <GraduationCap size={20} style={{ color: '#06B6D4' }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#06B6D4', fontFamily: 'Montserrat, sans-serif' }}>
                Diploma Autenticado — Estácio de Sá
              </span>
            </div>
            <p className="text-xs font-mono" style={{ color: '#6B7280' }}>
              {diploma.numeroDiploma}
            </p>
            <p className="text-xs mt-1" style={{ color: '#06B6D4' }}>Clique para visualizar</p>
          </div>
        </button>

        {/* Diploma Image Modal */}
        {diplomaModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
            onClick={() => setDiplomaModalOpen(false)}
          >
            <div
              className="relative max-w-4xl w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setDiplomaModalOpen(false)}
                className="absolute -top-10 right-0 text-white text-3xl font-bold hover:text-gray-300 focus:outline-none"
                aria-label="Fechar"
              >
                &times;
              </button>
              <img
                src="/assets/images/Diploma_engenharia_Controle_e_Automacao-1787016969560.jpeg"
                alt="Diploma de Engenharia de Controle e Automação - Estácio de Sá"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            onClick={onNovaConsulta}
            className="gov-btn-primary flex-1 justify-center"
            aria-label="Realizar nova consulta de diploma"
          >
            <RotateCcw size={16} aria-hidden="true" />
            Nova Consulta
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="gov-btn-secondary justify-center"
            aria-label="Imprimir resultado da consulta"
          >
            <Printer size={16} aria-hidden="true" />
            Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}